import React, { FC } from 'react';
import { SERVICE_TYPE } from '@baemingo/order-status-events/dist/types';
import { StorageData, useStorage } from '~Components/Storage';
import Toggle from '~ComponentsImproved/Toggle';
import SelectList from '~ComponentsImproved/SelectList';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useButton, useI18n, useLicenses, useLogger, useSelector, useStaffCan } from '~Hooks';
import { PageType, SettingsBlock } from '~Scenes/Settings-v2';
import { HIDE_IN, TogglesConfig, isHiddenOnDevice } from '~Scenes/Settings-v2/types';
import { BlueButton, Box } from '~Styles';
import { Languages } from '~Types';
import DeviceSettings from './DeviceSettings';
import Logout from '~Scenes/Settings-v2/Components/Logout';

/** Native names, same as the language modal in the tablet top bar */
const LANGUAGES: Array<{ id: Languages; name: string }> = [
  { id: 'en', name: 'English' },
  { id: 'se', name: 'Svenska' },
  { id: 'es', name: 'Español' },
  { id: 'no', name: 'Norsk' },
  { id: 'dk', name: 'Dansk' },
  { id: 'tr', name: 'Türkçe' },
];

interface Props {
  setSelectedPage: (page: PageType | 'developer') => void;
  /** Set by the Handy shell; the tablet route renders without it */
  isHandy?: boolean;
  /** Handheld only: opens the Adyen terminal mode page */
  onOpenTerminal?: () => void;
}

const OtherSettings: FC<Props> = ({ setSelectedPage, isHandy = false, onOpenTerminal }) => {
  const { i18n, changeLanguage } = useI18n();
  const locale = useSelector((state) => state.locale);
  const country = useSelector((state) => state.country);
  const [storage, commitToStorage, storageLoading] = useStorage();
  const flags = useSelector((state) => state.companyFlags);
  const licenses = useLicenses();
  const logger = useLogger('settings-v2');
  const staffCan = useStaffCan();

  function updateStorage(data: Partial<StorageData>) {
    logger.info('Updating Settings (Other Settings)', { fields: data });

    commitToStorage(data);
  }

  const update = (data: Partial<StorageData>) => {
    logger.info('Updating Settings (Other Settings)', { fields: { data } });

    if ('serviceType' in data) {
      if (data.serviceType) {
        // If it is true that means it is take away is default
        updateStorage({ serviceType: SERVICE_TYPE.TAKE_AWAY });
      } else {
        updateStorage({ serviceType: SERVICE_TYPE.EAT_HERE });
      }
    } else {
      updateStorage(data);
    }
  };

  const handleChangeLanguage = (id: string) => {
    changeLanguage(id as Languages);
    logger.info(`Changed language to ${id}`);
  };

  const togglesConfig: Array<TogglesConfig> = [
    {
      label: i18n('Settings.DefaultTakeAway'),
      key: 'serviceType',
      storage: storage.serviceType === SERVICE_TYPE.TAKE_AWAY,
    },
    {
      label: i18n('Settings.HideServiceType'),
      subLabel: i18n('Settings.HideServiceType.Text'),
      key: 'hideServiceType',
      storage: storage.hideServiceType,
    },
    {
      label: i18n('Settings.DisableAdyenPrintRetry'),
      subLabel: i18n('Settings.DisableAdyenPrintRetry.Text'),
      key: 'disableAdyenPrintRetry',
      storage: storage.disableAdyenPrintRetry,
    },
    {
      label: i18n('Settings.AlwaysPrintParkedOrder'),
      subLabel: i18n('Settings.AlwaysPrintParkedOrder.Text'),
      key: 'alwaysPrintParkedOrder',
      storage: storage.alwaysPrintParkedOrder,
      /** ES have a different behaviour when parking orders */
      hideIn: country === 'ES' ? HIDE_IN.BOTH : HIDE_IN.HANDY,
    },
    {
      label: i18n('Settings.AlwaysPrintReceiptEPOS'),
      key: 'alwaysPrintReceiptEPOS',
      storage: storage.alwaysPrintReceiptEPOS,
      hideIn: !licenses.hasEPOS ? HIDE_IN.BOTH : HIDE_IN.HANDY,
    },
    {
      label: i18n('Settings.ServiceTypeDelivery.Text'),
      key: 'enableServiceTypeDelivery',
      storage: storage.enableServiceTypeDelivery,
      hideIn: !licenses.hasKDS ? HIDE_IN.BOTH : HIDE_IN.HANDY,
    },
    {
      label: i18n('Settings.PrintChequeQR'),
      subLabel: i18n('Settings.PrintChequeQR.Text'),
      key: 'printQROnCheque',
      storage: storage.printQROnCheque,
      hideIn: !flags.hasPoints ? HIDE_IN.BOTH : undefined,
    },
  ];

  const devButton = useButton({
    onClick: () => setSelectedPage('developer'),
  });

  const terminalButton = useButton({
    onClick: () => onOpenTerminal?.(),
  });

  if (storageLoading) {
    return null;
  }

  /** Renders a toggle row from a toggle config entry. */
  const renderToggle = (toggle: TogglesConfig) =>
    !isHiddenOnDevice(toggle.hideIn, isHandy) && (
      <Toggle
        key={toggle.key}
        settingKey={toggle.key}
        label={`${toggle.label} ${storage.devMode ? '[' + toggle.key + ']' : ''} `}
        description={toggle.subLabel}
        initialValue={toggle.inverted ? !toggle.storage : toggle.storage}
        onToggle={() => update({ [toggle.key]: !toggle.storage })}
        fullWidth
        darkBackground
        lightText
        compact={isHandy}
      />
    );

  // Tablets pick the language from the top bar, handhelds do it here
  const showLanguage = isHandy;
  // Handheld reaches terminal mode from here once Adyen terminals are configured
  const showTerminal = isHandy && storage.adyenTerminals?.length > 0 && Boolean(onOpenTerminal);

  return (
    <Box padding={isHandy ? 'none' : 'large'} marginTop={isHandy ? 'small' : 'none'}>
      {!isHandy && (
        <Box>
          <Heading level={4} bottom={8} color="var(--colorWhite)">
            {i18n('Settings.Title')}
          </Heading>
        </Box>
      )}
      <SettingsBlock compact={isHandy}>
        {showLanguage && (
          <SelectList
            compact
            label={i18n('Handy.Language')}
            list={LANGUAGES}
            selectedId={locale}
            onSelect={handleChangeLanguage}
          />
        )}
        {togglesConfig.map(renderToggle)}
      </SettingsBlock>

      {showTerminal && (
        <Box marginTop={isHandy ? 'medium' : 'xl'}>
          <Heading level={isHandy ? 6 : 4} bottom={8} color="var(--colorWhite)">
            {i18n('Settings.TerminalSettings')}
          </Heading>
          <BlueButton size={isHandy ? 'small' : 'medium'} {...terminalButton}>
            {i18n('Settings.Payments.Open')}
          </BlueButton>
        </Box>
      )}

      <Box marginTop={isHandy ? 'medium' : 'xl'} marginBottom={isHandy ? 'medium' : 'xl'}>
        <Heading level={isHandy ? 6 : 4} bottom={8} color="var(--colorWhite)">
          {i18n('Settings.Device')}
        </Heading>
        <DeviceSettings isHandy={isHandy} />
      </Box>

      <Box marginTop={isHandy ? 'medium' : 'none'} marginBottom={isHandy ? 'medium' : 'xl'}>
        <Heading level={isHandy ? 6 : 4} bottom={8} color="var(--colorWhite)">
          {i18n('Settings.developer')}
        </Heading>
        <BlueButton {...devButton}>Developer</BlueButton>
      </Box>

      {staffCan.LOG_OUT && (
        <Box marginBottom={isHandy ? 'medium' : 'xl'}>
          <Heading level={isHandy ? 6 : 4} bottom={8} color="var(--colorWhite)">
            {i18n('Settings.LogoutTitle')}
          </Heading>
          <Logout />
        </Box>
      )}
    </Box>
  );
};

export default OtherSettings;
